import { Box, Stack } from "@mui/material";
import { Icon } from "components";
import { useTheme } from "contexts";
import { Socials } from "assets/data";
import { SocialType } from "types";
import { EnumTheme } from "utils/enums";

type Props = {
  color?: string;
  size?: "small" | "medium" | "large" | undefined;
};

export const Social = ({ size = "medium", color, ...props }: Props) => {
  const { theme } = useTheme();
  const isDark = theme.id === EnumTheme.Dark;

  // Configuration mapped according to the design system size prop
  const config = {
    small: {
      gap: theme.spacing.xxs,
      px: theme.spacing.xs,
      py: theme.spacing.xxs,
      fontSize: theme.font.xxxs,
      iconSize: 12,
      shadow: "1.5px 1.5px 0px 0px",
      hoverShadow: "2.5px 2.5px 0px 0px",
      letterSpacing: "0.05em",
    },
    medium: {
      gap: theme.spacing.xs,
      px: theme.spacing.sm,
      py: theme.spacing.xxs,
      fontSize: theme.font.xxs,
      iconSize: 14,
      shadow: "2px 2px 0px 0px",
      hoverShadow: "3px 3px 0px 0px",
      letterSpacing: "0.06em",
    },
    large: {
      gap: theme.spacing.sm,
      px: theme.spacing.md,
      py: theme.spacing.xs,
      fontSize: theme.font.xs,
      iconSize: 18,
      shadow: "3px 3px 0px 0px",
      hoverShadow: "4px 4px 0px 0px",
      letterSpacing: "0.08em",
    },
  }[size] || {
    gap: theme.spacing.xs,
    px: theme.spacing.sm,
    py: theme.spacing.xxs,
    fontSize: theme.font.xxs,
    iconSize: 14,
    shadow: "2px 2px 0px 0px",
    hoverShadow: "3px 3px 0px 0px",
    letterSpacing: "0.06em",
  };

  const textColor = color || theme.color.font.color;

  return (
    <Stack direction="row" spacing={theme.spacing.xs} flexWrap="wrap" gap={theme.spacing.xxs}>
      {Socials.map((item: SocialType) => {
        // Tag abbreviation for retro look
        const abbr =
          item.name.toLowerCase().includes("git") ? "GIT" :
          item.name.toLowerCase().includes("link") ? "LNK" :
          item.name.toLowerCase().includes("cv") || item.name.toLowerCase().includes("resume") ? "RCV" :
          item.name.toLowerCase().includes("mail") ? "MSG" :
          item.name.substring(0, 3).toUpperCase();

        return (
          <Box
            key={item.name}
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            href={item.href}
            aria-label={item.name}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: config.gap,
              px: config.px,
              py: config.py,
              backgroundColor: theme.color.background.accent,
              border: `1px solid ${theme.color.border.color}`,
              boxShadow: `${config.shadow} ${theme.color.border.color}`,
              color: textColor,
              fontFamily: '"Space Mono", monospace',
              fontSize: config.fontSize,
              fontWeight: theme.font.bold,
              textDecoration: "none",
              borderRadius: theme.border.square,
              transition: "transform 0.12s ease-in-out, box-shadow 0.12s ease-in-out, border-color 0.12s ease-in-out, color 0.12s ease-in-out",
              "&:hover": {
                transform: "translate(-1px, -1px)",
                borderColor: isDark ? "#ffffff" : "#191b22",
                boxShadow: `${config.hoverShadow} ${isDark ? "#000000" : "#191b22"}`,
                color: theme.color.primary.color,
              },
              "&:active": {
                transform: "translate(1px, 1px)",
                boxShadow: "none",
              },
            }}
          >
            <Icon.Svg
              name={item.icon}
              size={config.iconSize}
              fill={textColor}
            />
            <Box component="span" sx={{ letterSpacing: config.letterSpacing }}>
              {abbr}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};